/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, ReactNode, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"

import { EMPTY } from "common/constants/misc"
import { LocaleContent, LocaleContext } from "common/utils/i18n"
import { fetchLocale } from "utils/locale"
import { storageHelper } from "utils/storage"

interface LocaleProviderProps {
  children: ReactNode
}

/**
 * Supplies `LocaleContext` to the sliver of the app shell that renders ABOVE the
 * auth gate (see `RootRouter`), where `HomeContainer` — the app's other
 * LocaleContext provider — has not mounted. Its consumer is the session-error
 * screen, which is shown precisely when the gate fails; without a provider it
 * would fall back to the context default (empty content) and show its English
 * copy to every merchant.
 *
 * Narrower than `HomeContainer`'s provider on purpose: that one additionally
 * owns `isLocaleLoading` for `NavigationContainer` and the `I18n` globals
 * troy-common's sidebar reads, neither of which exists above the gate. Folding
 * the two into one provider is a refactor of the authenticated shell, not part
 * of this fix.
 *
 * Must be rendered inside a `Router` — it subscribes to navigation, see below.
 */
export const LocaleProvider: FC<LocaleProviderProps> = ({ children }) => {
  // Subscribes this subtree to navigation, which is what makes the read below
  // re-sample. Without it the provider never re-renders in production: `App` is
  // stateless, so `RootRouter` renders once, and react-router's `Router` passes
  // `this.props.children` straight through (react-router/cjs/react-router.js),
  // so React sees an identical element reference here and bails out of the
  // subtree — only `RouterContext` consumers like `Switch` re-render.
  useLocation()
  // Read per render rather than seeded once into state: nothing above the auth
  // gate calls `switchLocale` — the only caller is `NavigationContainer`, which
  // consumes `HomeContainer`'s provider — so a language switched mid-session on
  // a route that does not reload would otherwise leave this provider on the old
  // bundle for the rest of the page's life, and the session screen would come
  // up in the previous language.
  const currentLocale = storageHelper.getLang()
  const [localeContent, setLocaleContent] = useState<LocaleContent>(EMPTY)
  // Lags `currentLocale` until the bundle lands, so the locale on the context
  // always describes the content beside it.
  const [locale, setLocale] = useState<string>(currentLocale)
  // Lets a consumer force a reload without a locale change; kept for parity
  // with `HomeContainer`'s provider, which the sidebar switcher drives.
  const [switchLocaleTriggered, setSwitchLocaleTriggered] = useState(0)
  const switchLocale = () => {
    setSwitchLocaleTriggered(val => val + 1)
  }

  useEffect(() => {
    // Now that a locale change can arrive while a bundle is in flight, the
    // older load must not be allowed to land last and put the context back on
    // the previous language.
    let cancelled = false

    fetchLocale(currentLocale, (content: LocaleContent) => {
      if (cancelled) return
      setLocaleContent(content)
      setLocale(currentLocale)
    })

    return () => {
      cancelled = true
    }
  }, [currentLocale, switchLocaleTriggered])

  return (
    <LocaleContext.Provider value={{ locale, localeContent, switchLocale }}>
      {children}
    </LocaleContext.Provider>
  )
}
